import React, { useState } from "react";
import SidebarAdmin from "./SidebarAdmin";
import AdminNavbar from "./AdminNavbar";
import PostAdminButton from "../Assets/PostAdminButton";
import ModalConfirmation from "../Assets/ModalConfirmation";
import { User } from "../Types/Types";
import AdminSearchBar from "../Assets/AdminSearchBar";
import AdminUserTable from "./AdminUserTable";
import UserModal from "./UserModal";
import { useTranslation } from "react-i18next";
import useUsers from "../Hooks/useUsers";

interface AdminUsersProps {
    lang: string;
}

const AdminUsers: React.FC<AdminUsersProps> = ({ lang }) => {
    const { t } = useTranslation();
    const [showUserModal, setShowUserModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [userToEdit, setUserToEdit] = useState<User | null>(null);
    const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
    const [toDeleteId, setToDeleteId] = useState<string | null>(null);
    const {
        users,
        loading,
        error,
        createUser,
        updateUser,
        deleteUser,
        searchQuery,
        setSearchQuery,
        setSortBy,
        setSortOrder
    } = useUsers();

    const handleSearch = (query: string) => setSearchQuery(query);

    const handleDeleteRequest = (id: string) => {
        setToDeleteId(id);
        setConfirmDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (toDeleteId) {
            await deleteUser(toDeleteId);
            setToDeleteId(null);
        }
        setConfirmDeleteModal(false);
    };

    const handleDeleteCancel = () => {
        setToDeleteId(null);
        setConfirmDeleteModal(false);
    };

    const handleOpenCreateModal = () => {
        setUserToEdit(null);
        setIsEditMode(false);
        setShowUserModal(true);
    };

    const handleOpenEditModal = (user: User) => {
        setUserToEdit(user);
        setIsEditMode(true);
        setShowUserModal(true);
    };

    const handleUserSubmit = async (data: any) => {
        if (isEditMode && userToEdit) {
            await updateUser(userToEdit._id, data);
        } else {
            await createUser(data);
        }
        setShowUserModal(false);
    };

    return (
        <div className="min-h-screen bg-gray-100 flex">
            <div className="w-80">
                <SidebarAdmin />
            </div>
            <div className="flex-1 flex flex-col">
                <AdminNavbar />
                <div className="flex-1 py-12 px-48 space-y-12 font-montserrat">
                    <h1 className="text-4xl font-bold text-brand-800">{t(`users.users`)}</h1>
                    <div className="flex justify-between items-center space-x-4">
                        <PostAdminButton onClick={handleOpenCreateModal} />
                        
                        <AdminSearchBar searchQuery={searchQuery} onSearch={handleSearch} />
                    </div>
                    <AdminUserTable
                        users={users}
                        loading={loading}
                        error={error}
                        onEdit={handleOpenEditModal}
                        onDelete={handleDeleteRequest}
                        onSortChange={(field, order: 'asc' | 'desc') => {
                            setSortBy(field);
                            setSortOrder(order);
                        }}
                        lang={lang}
                    />
                </div>
            </div>

            <UserModal
                isOpen={showUserModal}
                onClose={() => setShowUserModal(false)}
                onSubmit={handleUserSubmit}
                isEditMode={isEditMode}
                initialData={
                    userToEdit
                        ? {
                            name: userToEdit.name,
                            email: userToEdit.email,
                            role: userToEdit.role as 'user' | 'admin', // assert known value
                        }
                        : undefined
                }
            />

            <ModalConfirmation
                isOpen={confirmDeleteModal}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
};

export default AdminUsers;
